import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { ArrowRight, Star, TrendingUp, Users, Target, MapPin, Store, Stethoscope, GraduationCap, Building, ShoppingBag, Briefcase } from 'lucide-react';

// Static case studies data - no API dependency
const caseStudiesData = [
  // LOCAL SEO
  {
    id: 1,
    title: 'Local SEO–Led Revenue & Footfall Growth',
    client: 'Pali Village Restaurant',
    industry: 'Local Business',
    category: 'Local SEO',
    challenge: 'Despite a strong legacy and 500+ seating capacity, the restaurant had minimal digital visibility. Organic discovery was weak, Google Maps presence was inconsistent.',
    strategy: 'Full-scale Local SEO revamp (GMB optimization, category correction, reviews velocity), location-based keyword mapping & content, citation cleanup and authority building.',
    results: [
      'Page-1 Google visibility within 6 months',
      '180–220% increase in organic discovery',
      '2.5× growth in walk-in inquiries from Google Maps',
      'Sustained growth over 2 years'
    ],
    metrics: { traffic: '+220%', leads: '2.5×', ranking: 'Page 1' },
    icon: Store
  },
  {
    id: 2,
    title: 'Rapid Demand Creation via Influencer + Local SEO',
    client: 'Pali Village Lonavla',
    industry: 'Local Business',
    category: 'Local SEO',
    challenge: 'A newly launched destination restaurant in a competitive tourist market needed immediate traction, not long-term brand building alone.',
    strategy: 'Hyperlocal influencer collaborations, Instagram Reels + UGC amplification, Google Maps dominance through Local SEO, review acquisition funnels.',
    results: [
      '500–600 daily footfalls on weekdays',
      '3× increase in branded searches',
      'High-intent tourist discovery via Maps & social',
      'Positioned as "must-visit" in one tourist season'
    ],
    metrics: { footfall: '600/day', searches: '3×', timeline: '6 months' },
    icon: MapPin
  },
  {
    id: 3,
    title: 'Local Search Dominance Driving Retail Footfall',
    client: 'The Watch Store',
    industry: 'Local Business',
    category: 'Local SEO',
    challenge: 'Zero structured search presence in a highly competitive retail + service category.',
    strategy: 'Local SEO foundation from scratch, service-based discovery optimization, consistent social media presence, review & reputation management.',
    results: [
      '70%+ high-intent keywords in Top 3',
      'Multiple #1 positions for "near me" searches',
      '3× increase in store walk-ins',
      'Strong brand recall in local catchment area'
    ],
    metrics: { keywords: '70%+ Top 3', walkins: '3×', duration: '2 Years' },
    icon: Store
  },
  // HEALTHCARE
  {
    id: 4,
    title: 'Short-Burst Campaigns for Brand Aesthetics & Recall',
    client: 'Medscape India',
    industry: 'Healthcare',
    category: 'Social Media',
    challenge: 'Inconsistent visual identity and low engagement across social platforms during campaign windows.',
    strategy: 'Campaign-specific social media management, feed & content visual beautification, format optimization for engagement (carousels, reels, stat creatives).',
    results: [
      '2.1× engagement uplift during campaigns',
      '35–45% improvement in content saves & shares',
      'Enhanced brand perception on LinkedIn & Instagram',
      'Short campaigns delivered outsized brand recall'
    ],
    metrics: { engagement: '2.1×', saves: '+45%', platforms: 'LinkedIn/IG' },
    icon: Stethoscope
  },
  {
    id: 5,
    title: 'Multi-Location Clinic Chain Growth',
    client: 'Dermatology Clinic Chain',
    industry: 'Healthcare',
    category: 'Local SEO',
    challenge: 'Need to establish local search presence across 12 locations in competitive healthcare market.',
    strategy: 'Multi-location Local SEO strategy, Google Maps optimization for each clinic, review generation and management system.',
    results: [
      '3.2× appointment bookings via Local SEO',
      'Maps visibility across all 12 locations',
      'Review count increased by 260%',
      'Consistent patient acquisition across locations'
    ],
    metrics: { bookings: '3.2×', locations: '12', reviews: '+260%' },
    icon: Stethoscope
  },
  // E-COMMERCE
  {
    id: 6,
    title: 'Organic Growth for UAE-Based Gifting Brand',
    client: 'Corporate Gifting E-commerce',
    industry: 'E-commerce',
    category: 'SEO',
    challenge: 'Low organic traction and underutilized Google Search Console insights in a competitive GCC market.',
    strategy: 'SEO-led content & category optimization, search intent mapping for B2B & festive gifting, CTR improvement via metadata & rich results.',
    results: [
      'Monthly clicks scaled from ~250 to 900–1,100',
      'Impressions grew from ~48K to 120K+',
      '2.8× organic traffic growth in 12 months',
      'Consistent visibility across peak seasons'
    ],
    metrics: { clicks: '4×', impressions: '120K+', growth: '2.8×' },
    icon: ShoppingBag
  },
  {
    id: 7,
    title: 'D2C Fashion Brand Revenue Growth',
    client: 'Fashion Brand (UK)',
    industry: 'E-commerce',
    category: 'SEO',
    challenge: 'Need to reduce dependency on paid ads and build sustainable organic revenue channel.',
    strategy: 'Category page optimization, content strategy for fashion keywords, technical SEO improvements, link building.',
    results: [
      '310% revenue growth from organic search',
      'Category pages became top revenue contributors',
      'Reduced paid ad dependency significantly',
      'Sustainable organic growth achieved'
    ],
    metrics: { revenue: '+310%', channel: 'Organic', market: 'UK' },
    icon: ShoppingBag
  },
  // EDUCATION
  {
    id: 8,
    title: 'Global Education Platform Enrollment Growth',
    client: 'Education Platform (Global)',
    industry: 'Education',
    category: 'SEO',
    challenge: 'Competitive exam preparation market with established players dominating search results.',
    strategy: 'Comprehensive keyword research, content optimization for competitive exam keywords, technical SEO, authority building.',
    results: [
      '5× increase in organic enrollments',
      'Ranked in Top 5 for competitive exam keywords',
      'Established as authority in niche',
      'Sustainable student acquisition channel'
    ],
    metrics: { enrollments: '5×', ranking: 'Top 5', reach: 'Global' },
    icon: GraduationCap
  },
  // REAL ESTATE
  {
    id: 9,
    title: 'Real Estate Developer Lead Generation',
    client: 'Real Estate Developer (India)',
    industry: 'Real Estate',
    category: 'SEO + CRO',
    challenge: 'High cost per lead and inconsistent quality of inbound inquiries.',
    strategy: 'SEO optimization for real estate keywords, landing page optimization, conversion rate optimization, lead qualification process.',
    results: [
      '2.5× inbound leads through SEO + landing optimization',
      'Average lead cost reduced by 41%',
      'Improved lead quality and conversion',
      'Sustainable lead generation channel'
    ],
    metrics: { leads: '2.5×', cost: '-41%', market: 'India' },
    icon: Building
  },
  // SAAS / B2B
  {
    id: 10,
    title: 'SaaS Demo Booking Optimization',
    client: 'SaaS Company (US)',
    industry: 'SaaS',
    category: 'SEO',
    challenge: 'High customer acquisition cost and low organic demo bookings.',
    strategy: 'BOFU keyword targeting, content strategy for decision-stage keywords, technical SEO, conversion optimization.',
    results: [
      '4× organic demo bookings in 14 months',
      'Ranked for 120+ BOFU keywords',
      'Reduced CAC by 38%',
      'Organic became primary demo channel'
    ],
    metrics: { demos: '4×', keywords: '120+', cac: '-38%' },
    icon: Briefcase
  },
  {
    id: 11,
    title: 'IT Services Enterprise Lead Growth',
    client: 'IT Services Company (APAC)',
    industry: 'B2B Services',
    category: 'SEO',
    challenge: 'Enterprise keywords stuck on page 4, minimal organic lead generation.',
    strategy: 'Enterprise keyword strategy, technical SEO fixes, content authority building, backlink acquisition.',
    results: [
      '180% growth in qualified inbound leads',
      'Enterprise keywords moved from page 4 → page 1',
      'Established thought leadership',
      'Reduced dependency on outbound sales'
    ],
    metrics: { leads: '+180%', ranking: 'Page 4→1', region: 'APAC' },
    icon: Briefcase
  },
  // WELLNESS
  {
    id: 12,
    title: 'Wellness Brand Local Discovery Growth',
    client: 'Wellness & Fitness Brand (India)',
    industry: 'Wellness',
    category: 'Local SEO + Social',
    challenge: 'Low local discovery and inconsistent social presence affecting conversions.',
    strategy: 'Local SEO optimization, social media content strategy, synergy between SEO and social channels.',
    results: [
      '4× local discovery growth',
      'Social + SEO synergy improved conversion rate by 52%',
      'Strong local brand presence established',
      'Consistent footfall growth'
    ],
    metrics: { discovery: '4×', conversion: '+52%', market: 'India' },
    icon: Users
  },
  // URBO LIFE (keeping from previous)
  {
    id: 13,
    title: 'Lifestyle Brand Digital Transformation',
    client: 'Urbo Life',
    industry: 'Lifestyle',
    category: 'Full Digital',
    challenge: 'New brand needed to establish digital presence from scratch in competitive lifestyle market.',
    strategy: 'Complete digital strategy including SEO, social media, content marketing, and paid campaigns.',
    results: [
      'Established strong digital presence',
      'Built engaged community on social',
      'Organic traffic growing consistently',
      'Brand recognition in target market'
    ],
    metrics: { presence: 'Established', growth: 'Consistent', community: 'Engaged' },
    icon: Star
  }
];

const testimonials = [
  {
    id: 1,
    name: 'Restaurant Owner',
    role: 'Founder',
    company: 'Pali Village',
    content: 'Digital Startup transformed our online presence. We went from being invisible on Google to dominating local search. Our walk-ins have increased dramatically.',
    rating: 5
  },
  {
    id: 2,
    name: 'Marketing Head',
    role: 'Director',
    company: 'Healthcare Brand',
    content: 'The team understands healthcare marketing compliance while delivering creative campaigns. Our engagement metrics have never been better.',
    rating: 5
  },
  {
    id: 3,
    name: 'E-commerce Manager',
    role: 'Head of Digital',
    company: 'UAE Gifting Co',
    content: 'Our organic traffic has grown 3x in just one year. The SEO strategy they implemented is now our primary customer acquisition channel.',
    rating: 5
  }
];

const categories = ['All', 'Local SEO', 'SEO', 'Social Media', 'SEO + CRO', 'Full Digital'];

export const Work = ({ onOpenAuditModal }) => {
  usePageMeta();
  const [activeFilter, setActiveFilter] = useState('All');

  const filteredCaseStudies = activeFilter === 'All' 
    ? caseStudiesData 
    : caseStudiesData.filter(cs => cs.category === activeFilter);

  return (
    <main className="pt-20" data-testid="work-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Our Work
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] mb-4">
              Results That Speak for Themselves
            </h1>
            <p className="text-base md:text-lg text-[#525252]">
              Explore our case studies and see how we've helped businesses across industries 
              achieve measurable growth through SEO, Local SEO, and Digital Marketing.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-[#2E2E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <TrendingUp className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">310%</p>
              <p className="text-sm text-gray-400">Max Revenue Growth</p>
            </div>
            <div className="text-center">
              <Users className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">150+</p>
              <p className="text-sm text-gray-400">Happy Clients</p>
            </div>
            <div className="text-center">
              <Target className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">120+</p>
              <p className="text-sm text-gray-400">BOFU Keywords Ranked</p>
            </div>
            <div className="text-center">
              <Star className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-2xl md:text-3xl font-bold text-white">95%</p>
              <p className="text-sm text-gray-400">Client Retention</p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 md:py-24 bg-[#F7F7F7]" data-testid="case-studies-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Case Studies
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Success Stories Across Industries
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === category
                    ? 'bg-[#FFAA17] text-white'
                    : 'bg-white text-[#525252] hover:bg-gray-100'
                }`}
                data-testid={`filter-${category}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Case Study Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCaseStudies.map((caseStudy) => {
              const Icon = caseStudy.icon;
              return (
                <div
                  key={caseStudy.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group"
                  data-testid={`case-study-${caseStudy.id}`}
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#FFAA17]/10 rounded-xl flex items-center justify-center group-hover:bg-[#FFAA17] transition-colors">
                        <Icon className="w-6 h-6 text-[#FFAA17] group-hover:text-white transition-colors" />
                      </div>
                      <span className="bg-[#F7F7F7] text-[#525252] text-xs font-medium px-3 py-1 rounded-full">
                        {caseStudy.category}
                      </span>
                    </div>

                    {/* Content */}
                    <p className="text-[#FFAA17] font-semibold text-sm mb-1">{caseStudy.client}</p>
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-3 leading-tight">{caseStudy.title}</h3>
                    <p className="text-[#525252] text-sm mb-4 line-clamp-2">{caseStudy.challenge}</p>
                    
                    {/* Results */}
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-xs font-semibold text-[#1A1A1A] uppercase mb-3">Key Results</p>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(caseStudy.metrics).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <p className="text-base font-bold text-[#FFAA17]">{value}</p>
                            <p className="text-xs text-[#525252] capitalize">{key}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white" data-testid="testimonials-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Testimonials
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-[#F7F7F7] rounded-2xl p-8"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-[#525252] mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-[#1A1A1A]">{testimonial.name}</p>
                  <p className="text-sm text-[#525252]">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#2E2E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Want Similar Results for Your Business?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve measurable growth. 
            Get a free growth audit today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onOpenAuditModal}
              className="bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full px-8 py-3"
              data-testid="work-cta-primary"
            >
              Get Free Growth Audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-3"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Work;
